/**
 * The Flows card's draft — pure, so the rules the card follows are tested
 * without a DOM. The server enforces all of them again (`setTenantFlows`); the
 * card only avoids offering what it would refuse.
 *
 * An edit is a draft on a BASE: the view it was made from. Saving sends the
 * base's version, so if the company's flows changed since, the server refuses
 * and the person reloads rather than overwriting someone else's choice. A newer
 * view replaces the base only while there is nothing unsaved (`nextEdit`): a
 * refetch caused by another card on the page must not throw ticks away.
 */
import type { TenantFlowAccessMode, TenantFlowOption, TenantFlowsView } from '@portal/contracts'

export interface FlowsDraft {
  mode: TenantFlowAccessMode
  chosen: ReadonlySet<string>
}

export interface FlowsEdit {
  base: TenantFlowsView
  draft: FlowsDraft
}

/** The draft as the server last answered it. */
export function draftOf(view: TenantFlowsView): FlowsDraft {
  return { mode: view.mode, chosen: new Set(view.flows.filter((f) => f.chosen).map((f) => f.flowId)) }
}

export function editOf(view: TenantFlowsView): FlowsEdit {
  return { base: view, draft: draftOf(view) }
}

/** Whether the draft differs from its base. */
export function draftChanged(base: TenantFlowsView, draft: FlowsDraft): boolean {
  const saved = draftOf(base)
  if (saved.mode !== draft.mode || saved.chosen.size !== draft.chosen.size) return true
  for (const id of draft.chosen) if (!saved.chosen.has(id)) return true
  return false
}

/** A newer view of the company: taken as the base unless the person has unsaved changes. */
export function nextEdit(prev: FlowsEdit | null, view: TenantFlowsView): FlowsEdit {
  if (!prev || !draftChanged(prev.base, prev.draft)) return editOf(view)
  return prev
}

/** Never chosen (version 0): saving records a choice even when nothing changed — keeping every flow is one too. */
export function canSave(edit: FlowsEdit): boolean {
  return draftChanged(edit.base, edit.draft) || edit.base.version === 0
}

/** fr-docs could not be asked, so whether each flow is offered is unknown, not "no". */
export function offerUnknown(view: TenantFlowsView): boolean {
  return view.offeredError !== undefined
}

/**
 * Whether the box can be ticked or unticked now. A flow not known to be offered can only be
 * taken away — choosing it would be refused — and under `all` the boxes do not apply.
 */
export function canToggle(option: TenantFlowOption, draft: FlowsDraft): boolean {
  if (draft.mode !== 'selected') return false
  return option.offered || draft.chosen.has(option.flowId)
}

export function toggled(draft: FlowsDraft, flowId: string): FlowsDraft {
  const chosen = new Set(draft.chosen)
  if (chosen.has(flowId)) chosen.delete(flowId)
  else chosen.add(flowId)
  return { ...draft, chosen }
}

/**
 * The rows the card lists: under `selected` every offered flow plus the chosen ones not known to
 * be offered (so they can be removed); under `all`, the offered flows only — what its people see —
 * since the kept choice does not apply there.
 */
export function rowsFor(view: TenantFlowsView, draft: FlowsDraft): TenantFlowOption[] {
  return draft.mode === 'all' ? view.flows.filter((f) => f.offered) : view.flows
}

/**
 * How many flows the company's people would see with this draft; null when fr-docs could not be
 * asked under `all`, where the answer is "whatever it offers".
 */
export function visibleCount(view: TenantFlowsView, draft: FlowsDraft): number | null {
  if (offerUnknown(view)) return draft.mode === 'all' ? null : draft.chosen.size
  const offered = view.flows.filter((f) => f.offered)
  return draft.mode === 'all' ? offered.length : offered.filter((f) => draft.chosen.has(f.flowId)).length
}

/** The request body: the chosen ids sorted, on the version the draft was made from. */
export function saveBody(edit: FlowsEdit) {
  return { mode: edit.draft.mode, flowIds: [...edit.draft.chosen].sort(), version: edit.base.version }
}
